import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiSearch, FiUser, FiLogIn, FiHome, FiGrid, FiX, FiSliders } from "react-icons/fi";
import { BsHandbag } from "react-icons/bs";
import { useSelector, useDispatch } from "react-redux";
import Search from "./Search";
import SizeFinderQuick from "./SizeFinderQuick";
import CartDrawer from "./CartDrawer";
import GlobalTopbar from "./GlobalTopbar";
import { toggleSearchBar } from "../../store/reducers/globalReducer";
import { logout } from "../../store/reducers/authReducer";
import { useUserLogoutMutation } from "../../store/services/authService";

const navItems = [
  { title: "خانه", url: "/" },
  { title: "همه محصولات", url: "/products" },
  { title: "سایز من", action: "size", highlight: true },
  { title: "صندل زنانه", url: "/category/women-sandals" },
  { title: "کفش مجلسی", url: "/category/women-formal-shoes" },
  { title: "کفش پرسنلی", url: "/category/women-work-shoes" },
  { title: "اسپورت", url: "/category/women-sport-shoes" }
];

const BrandLogo = () => (
  <>
    <div className="brand-mark">
      <span>J</span>
    </div>

    <div className="brand-copy">
      <strong>جوتی</strong>
      <span>JOOTI</span>
    </div>
  </>
);

const Nav = () => {
  const [open, setOpen] = useState(false);
  const [sizePopupOpen, setSizePopupOpen] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [cartPulse, setCartPulse] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  const { pathname } = useLocation();
  const { userToken, user } = useSelector((state) => state.authReducer);
  const { items } = useSelector((state) => state.cartReducer);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userLogout] = useUserLogoutMutation();

  const closeMenu = () => setOpen(false);

  const accountUrl = userToken ? "/user" : "/login";
  const decodedPathname = decodeURIComponent(pathname);
  const isCategoryActive = decodedPathname.startsWith("/category") || decodedPathname.startsWith("/cat-products");

  const openSizePopup = () => {
    closeMenu();
    setSizePopupOpen(true);
  };

  const closeAccountMenu = () => setAccountOpen(false);

  const handleUserLogout = async () => {
    try {
      await userLogout().unwrap();
    } catch {}

    dispatch(logout("userToken"));
    closeAccountMenu();
    navigate("/login");
  };


  useEffect(() => {
    const handleCartAdded = () => {
      setCartPulse(true);

      setCartDrawerOpen(true);

      window.clearTimeout(window.__jootiCartPulseTimer);

      window.__jootiCartPulseTimer = window.setTimeout(() => {
        setCartPulse(false);
      }, 850);
    };

    window.addEventListener("jooti:cart-added", handleCartAdded);

    return () => {
      window.removeEventListener("jooti:cart-added", handleCartAdded);
    };
  }, []);

  const renderNavItem = (item) => {
    if (item.action === "size") {
      return (
        <button
          type="button"
          className={`nav-link nav-link-button ${item.highlight ? "nav-size-link" : ""}`}
          onClick={openSizePopup}
        >
          {item.title}
        </button>
      );
    }

    const isActive = item.url && (
      decodedPathname === item.url ||
      decodedPathname.startsWith(`${item.url}/`)
    );

    return (
      <Link
        to={item.url}
        className={`nav-link ${item.highlight ? "nav-size-link" : ""} ${isActive ? "nav-link-active" : ""}`}
        onClick={closeMenu}
      >
        {item.title}
      </Link>
    );
  };

  const renderMobileCategoryItem = (item) => {
    if (item.action === "size") {
      return (
        <button
          type="button"
          key={item.title}
          onClick={openSizePopup}
          className="mobile-size-finder-link"
        >
          {item.title}
        </button>
      );
    }

    return (
      <Link to={item.url} key={item.url} onClick={closeMenu}>
        {item.title}
      </Link>
    );
  };

  return (
    <>
      <GlobalTopbar />

      <nav className="nav">
        <div className="site-container">
          <div className="nav-inner">

            <Link to="/" className="nav-logo" onClick={closeMenu}>
              <BrandLogo />
            </Link>

            <ul className="nav-menu">
              {navItems.map((item) => (
                <li key={item.url || item.title}>
                  {renderNavItem(item)}
                </li>
              ))}
            </ul>

            <div className="nav-actions">
              <button
                className="nav-icon-btn"
                type="button"
                onClick={() => dispatch(toggleSearchBar())}
                aria-label="جستجو"
                title="جستجو"
              >
                <FiSearch size={20} />
              </button>

              <div
                className={`nav-account-wrap ${accountOpen ? "active" : ""}`}
                onMouseEnter={() => setAccountOpen(true)}
                onMouseLeave={() => setAccountOpen(false)}
              >
                <button
                  type="button"
                  className="nav-icon-btn nav-user-btn"
                  title={userToken ? "پروفایل من" : "ورود به حساب"}
                  aria-label={userToken ? "پروفایل من" : "ورود به حساب"}
                  onClick={() => {
                    navigate(userToken ? "/user" : "/login");
                    setAccountOpen(false);
                  }}
                >
                  {userToken ? <FiUser size={20} /> : <FiLogIn size={20} />}
                </button>

                {userToken ? (
                  <div className="nav-account-popover">
                    <div className="nav-account-head">
                      <span>حساب کاربری</span>
                      <strong>{user?.name || "کاربر جوتی"}</strong>
                    </div>

                    <div className="nav-account-links">
                      <Link to="/user" onClick={closeAccountMenu}>
                        پروفایل من
                      </Link>

                      <Link to="/orders" onClick={closeAccountMenu}>
                        سفارش‌های من
                      </Link>

                      <button type="button" onClick={handleUserLogout}>
                        خروج
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="nav-cart-wrap">
                <Link
                  to="/cart"
                  className={`nav-icon-btn nav-cart-btn ${cartPulse ? "cart-pulse" : ""}`}
                  title="سبد خرید"
                  aria-label="سبد خرید"
                >
                  <BsHandbag size={19} />
                  <span>{items}</span>
                </Link>

                <CartDrawer compact />
              </div>
            </div>
          </div>
        </div>

        <div className="mobile-top-brand">
          <Link to="/" onClick={closeMenu} className="mobile-top-logo">
            <BrandLogo />
          </Link>

          <button
            type="button"
            onClick={() => dispatch(toggleSearchBar())}
            aria-label="جستجو"
          >
            <FiSearch size={19} />
            <span>جستجو</span>
          </button>
        </div>

        <div
          className={`mobile-category-backdrop ${open ? "active" : ""}`}
          onClick={closeMenu}
        />

        <div className={`mobile-category-sheet ${open ? "active" : ""}`}>
          <div className="mobile-category-sheet-head">
            <strong>دسته‌بندی‌ها</strong>

            <button
              type="button"
              onClick={closeMenu}
              aria-label="بستن دسته‌بندی"
            >
              <FiX size={20} />
            </button>
          </div>

          <div className="mobile-category-list">
            <Link to="/products" onClick={closeMenu}>
              همه محصولات
            </Link>

            {navItems
              .filter((item) => item.url && (item.url.startsWith("/category") || item.url.startsWith("/cat-products")))
              .map((item) => renderMobileCategoryItem(item))}
          </div>
        </div>

        <div className="mobile-bottom-nav">
          <Link to="/" onClick={closeMenu} className={pathname === "/" ? "active" : ""}>
            <FiHome size={21} />
            <span>خانه</span>
          </Link>

          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            className={isCategoryActive || open ? "active" : ""}
          >
            <FiGrid size={21} />
            <span>دسته‌بندی</span>
          </button>

          <button
            type="button"
            onClick={openSizePopup}
            className={pathname === "/size-finder" ? "active" : ""}
          >
            <FiSliders size={21} />
            <span>سایز من</span>
          </button>

          <Link
            to="/cart"
            onClick={closeMenu}
            className={pathname === "/cart" ? "active" : ""}
          >
            <span className={`mobile-cart-icon ${cartPulse ? "cart-pulse" : ""}`}>
              <BsHandbag size={21} />
              {items > 0 && <b>{items}</b>}
            </span>

            <span>سبد خرید</span>
          </Link>

          <Link
            to={accountUrl}
            onClick={closeMenu}
            className={pathname.startsWith("/user") || pathname === "/login" ? "active" : ""}
          >
            <FiUser size={21} />
            <span>{userToken ? "حساب" : "ورود"}</span>
          </Link>
        </div>
      </nav>

      <Search />

      <SizeFinderQuick
        open={sizePopupOpen}
        onClose={() => setSizePopupOpen(false)}
      />

      <CartDrawer
        open={cartDrawerOpen}
        onClose={() => setCartDrawerOpen(false)}
      />
    </>
  );
};

export default Nav;
